/** Facilities setup tabs: routing rules, and the facilities mailbox. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Route, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { useDesk } from '../lib/desk';
import { ROUTING_ASSIGNMENT_LABEL, type RoutingAssignment, type RoutingPreview, type RoutingRule, type RoutingRuleInput } from '../lib/facilities';
import type { EmailIntegration } from '../lib/types';
import { EmailConnection, EmailLog, EmailOptions } from './email-intake';
import { AssigneeSelect, CategorySelect, PlaceFields } from './facilities-ui';
import { IntegrationLogo } from './integration-logo';
import { IntegrationShell } from './integration-shell';
import { useToast } from './toast';
import { Alert, Badge, Button, Card, CardHeader, cn, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal, PageLoader, Select, Spinner, Switch } from './ui';

// ---------------------------------------------------------------------------
// Routing rules
// ---------------------------------------------------------------------------

type RuleDraft = {
  id?: string;
  name: string;
  enabled: boolean;
  departmentId: string;
  buildingId: string;
  roomId: string;
  assignment: RoutingAssignment;
  assignedToId: string;
  teamId: string;
};

const blankRule = (): RuleDraft => ({ name: '', enabled: true, departmentId: '', buildingId: '', roomId: '', assignment: 'ROUND_ROBIN', assignedToId: '', teamId: '' });

const draftOf = (rule: RoutingRule): RuleDraft => ({
  id: rule.id,
  name: rule.name,
  enabled: rule.enabled,
  departmentId: rule.departmentId ?? '',
  buildingId: rule.buildingId ?? '',
  roomId: rule.roomId ?? '',
  assignment: rule.assignment,
  assignedToId: rule.assignedToId ?? '',
  teamId: rule.teamId ?? '',
});

const inputOf = (draft: RuleDraft): RoutingRuleInput => ({
  name: draft.name.trim(),
  enabled: draft.enabled,
  departmentId: draft.departmentId || null,
  buildingId: draft.buildingId || null,
  roomId: draft.roomId || null,
  assignment: draft.assignment,
  assignedToId: draft.assignment === 'USER' ? draft.assignedToId || null : null,
  teamId: draft.assignment === 'USER' ? null : draft.teamId || null,
});

/** "Plumbing requests in Room 101, Main Hall" */
function describeMatch(rule: RoutingRule) {
  const what = rule.category ? `${rule.category.path} requests` : 'Any request';
  const where = rule.room ? ` in ${rule.room.name}${rule.building ? `, ${rule.building.name}` : ''}` : rule.building ? ` in ${rule.building.name}` : '';
  return `${what}${where}`;
}

function describeAssignment(rule: RoutingRule) {
  switch (rule.assignment) {
    case 'USER':
      return rule.assignedTo ? rule.assignedTo.name : 'someone who’s no longer on the team';
    case 'ROUND_ROBIN':
      return rule.team ? `the next available person on the ${rule.team.path} crew` : 'the next available person on the request’s crew';
    case 'TEAM_LEAD':
      return rule.team ? `the ${rule.team.path} crew’s lead` : 'the lead of the request’s crew';
  }
}

function RoutingRuleModal({ draft, onClose }: { draft: RuleDraft | null; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RuleDraft | null>(draft);
  const [error, setError] = useState('');
  useEffect(() => {
    setForm(draft);
    setError('');
  }, [draft]);

  const save = useMutation({
    mutationFn: (value: RuleDraft) =>
      value.id ? api.put<RoutingRule>(`/facilities/routing-rules/${value.id}`, inputOf(value)) : api.post<RoutingRule>('/facilities/routing-rules', inputOf(value)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      toast.success('Routing rule saved');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  if (!form) return null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (form.assignment === 'USER' && !form.assignedToId) {
      setError('Choose who gets these requests');
      return;
    }
    save.mutate(form);
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={form.id ? `Edit ${form.name}` : 'Add a routing rule'}
      description="A request matches when it’s in the category (or one inside it), building, and room you choose. Anything left empty matches everything."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="routing-rule-form" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="routing-rule-form" onSubmit={submit} className="space-y-5">
        {error && <Alert tone="red">{error}</Alert>}
        <Field label="Name" required>
          <Input
            value={form.name}
            maxLength={120}
            required
            autoFocus
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Annex plumbing goes to Tia"
          />
        </Field>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Requests that match</h3>
          <Field label="Category">
            <CategorySelect value={form.departmentId} onChange={(departmentId) => setForm({ ...form, departmentId })} emptyLabel="Any category" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <PlaceFields
              buildingId={form.buildingId}
              roomId={form.roomId}
              emptyBuilding="Any building"
              onChange={(next) => setForm({ ...form, ...next })}
            />
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Go to</h3>
          <Field label="Assign to">
            <Select value={form.assignment} onChange={(e) => setForm({ ...form, assignment: e.target.value as RoutingAssignment })}>
              {(Object.keys(ROUTING_ASSIGNMENT_LABEL) as RoutingAssignment[]).map((assignment) => (
                <option key={assignment} value={assignment}>
                  {ROUTING_ASSIGNMENT_LABEL[assignment]}
                </option>
              ))}
            </Select>
          </Field>
          {form.assignment === 'USER' ? (
            <Field label="Person" required hint="Facilities technicians and administrators. If they’re away, the next rule gets the request.">
              <AssigneeSelect value={form.assignedToId} onChange={(assignedToId) => setForm({ ...form, assignedToId })} emptyLabel="Choose someone" />
            </Field>
          ) : (
            <Field
              label="Crew"
              hint={
                form.assignment === 'TEAM_LEAD'
                  ? 'The crew’s lead gets the request, or the next available person when the lead is away.'
                  : 'People on the crew take turns. Anyone away is skipped.'
              }
            >
              <CategorySelect value={form.teamId} onChange={(teamId) => setForm({ ...form, teamId })} emptyLabel="The request’s own category" />
            </Field>
          )}
        </section>

        <Switch checked={form.enabled} onChange={(enabled) => setForm({ ...form, enabled })} label="Turned on" description="Rules that are off are skipped." />
      </form>
    </Modal>
  );
}

function RoutingPreviewCard() {
  const [departmentId, setDepartmentId] = useState('');
  const [place, setPlace] = useState({ buildingId: '', roomId: '' });
  const preview = useQuery({
    queryKey: ['facilities', 'routing-preview', departmentId, place.buildingId, place.roomId],
    queryFn: () =>
      api.post<RoutingPreview>('/facilities/routing-rules/preview', {
        departmentId: departmentId || null,
        buildingId: place.buildingId || null,
        roomId: place.roomId || null,
      }),
  });

  return (
    <Card className="h-fit">
      <CardHeader title="Where would a request go?" description="Try a category and a place to see which rule picks it up and who would get it right now." />
      <div className="space-y-3 p-5">
        <Field label="Category">
          <CategorySelect value={departmentId} onChange={setDepartmentId} />
        </Field>
        <PlaceFields buildingId={place.buildingId} roomId={place.roomId} onChange={setPlace} />
        {preview.isLoading ? (
          <Spinner />
        ) : preview.error ? (
          <Alert tone="red">{errorMessage(preview.error)}</Alert>
        ) : preview.data?.assignee ? (
          <Alert tone="green" title={preview.data.assignee.name}>
            By the rule “{preview.data.rule?.name}”{preview.data.assignee.note ? ` (${preview.data.assignee.note})` : ''}.
          </Alert>
        ) : (
          <Alert tone="amber" title="Nobody right now">
            No rule matches with someone available, so the request would wait in the unassigned queue.
          </Alert>
        )}
        {!!preview.data?.passedOver.length && (
          <p className="text-xs text-slate-500">Passed over because nobody was available: {preview.data.passedOver.map((rule) => rule.name).join(', ')}.</p>
        )}
      </div>
    </Card>
  );
}

/** The rules that assign facilities requests, checked in order. */
export function RoutingRulesTab() {
  const desk = useDesk();
  const canEdit = desk.can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const rules = useQuery({ queryKey: ['facilities', 'routing-rules'], queryFn: () => api.get<RoutingRule[]>('/facilities/routing-rules') });
  const [editing, setEditing] = useState<RuleDraft | null>(null);
  const [deleting, setDeleting] = useState<RoutingRule | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['facilities'] });
  const onError = (err: unknown) => toast.error(errorMessage(err));

  const toggle = useMutation({
    mutationFn: (rule: RoutingRule) => api.put(`/facilities/routing-rules/${rule.id}`, { ...inputOf(draftOf(rule)), enabled: !rule.enabled }),
    onSuccess: (_result, rule) => {
      void refresh();
      toast.success(rule.enabled ? `Turned off “${rule.name}”` : `Turned on “${rule.name}”`);
    },
    onError,
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.post<RoutingRule[]>('/facilities/routing-rules/reorder', { ids }),
    onSuccess: (next) => {
      queryClient.setQueryData(['facilities', 'routing-rules'], next);
      void refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (rule: RoutingRule) => api.delete(`/facilities/routing-rules/${rule.id}`),
    onSuccess: () => {
      void refresh();
      setDeleting(null);
      toast.success('Routing rule deleted');
    },
    onError: (err) => {
      setDeleting(null);
      onError(err);
    },
  });

  const move = (index: number, delta: number) => {
    const ids = rules.data!.map((rule) => rule.id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + delta, 0, id);
    reorder.mutate(ids);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <Card>
        <CardHeader
          title="Routing rules"
          description="When a request comes in without an assignee, these rules are checked from the top. The first one that matches it and finds someone available assigns it."
          actions={
            canEdit && (
              <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditing(blankRule())}>
                Add rule
              </Button>
            )
          }
        />
        {rules.isLoading ? (
          <PageLoader />
        ) : rules.error ? (
          <ErrorState error={rules.error} onRetry={() => rules.refetch()} />
        ) : rules.data!.length === 0 ? (
          <EmptyState
            icon={<Route />}
            title="No routing rules"
            description="Without rules, requests wait in the unassigned queue until someone picks them up."
            action={
              canEdit && (
                <Button variant="primary" icon={<Plus />} onClick={() => setEditing(blankRule())}>
                  Add rule
                </Button>
              )
            }
          />
        ) : (
          <ol className="divide-y divide-slate-100">
            {rules.data!.map((rule, index) => (
              <li key={rule.id} className={cn('flex flex-wrap items-center gap-3 px-5 py-3', !rule.enabled && 'bg-slate-50/60')}>
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">{index + 1}</span>
                <div className={cn('min-w-0 flex-1', !rule.enabled && 'opacity-60')}>
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
                    {rule.name}
                    {!rule.enabled && <Badge>Off</Badge>}
                    {rule.matchesEverything && <Badge tone="blue">Catch-all</Badge>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {describeMatch(rule)} → {describeAssignment(rule)}
                  </p>
                </div>
                {canEdit && (
                  <div className="flex items-center whitespace-nowrap">
                    <Button size="sm" variant="ghost" icon={<ArrowUp />} disabled={index === 0 || reorder.isPending} onClick={() => move(index, -1)} aria-label={`Move ${rule.name} up`} />
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<ArrowDown />}
                      disabled={index === rules.data!.length - 1 || reorder.isPending}
                      onClick={() => move(index, 1)}
                      aria-label={`Move ${rule.name} down`}
                    />
                    <Button size="sm" variant="ghost" loading={toggle.isPending && toggle.variables?.id === rule.id} onClick={() => toggle.mutate(rule)}>
                      {rule.enabled ? 'Turn off' : 'Turn on'}
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setEditing(draftOf(rule))}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(rule)} aria-label={`Delete ${rule.name}`} />
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
        <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-500">
          Put rules for specific buildings, rooms, and categories above catch-all rules. A request nobody can take waits in the unassigned queue, which the Facilities overview
          shows.
        </p>
      </Card>

      <RoutingPreviewCard />

      <RoutingRuleModal draft={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
        loading={remove.isPending}
        title={`Delete “${deleting?.name}”?`}
        description="Requests it would have assigned go to the next rule that matches. Work orders it already assigned stay with their assignees."
        confirmLabel="Delete rule"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Facilities mailbox
// ---------------------------------------------------------------------------

/** Email to the facilities address becomes facilities requests. Facilities administrators manage it here. */
export function FacilitiesEmailTab() {
  const integration = useQuery({
    queryKey: ['integrations', 'FACILITIES_EMAIL'],
    queryFn: () => api.get<EmailIntegration>('/integrations/facilities-email'),
    refetchInterval: (q) => (q.state.data?.recentRuns.some((run) => run.status === 'QUEUED' || run.status === 'RUNNING') ? 3000 : false),
  });
  if (integration.isLoading) return <PageLoader />;
  if (integration.error || !integration.data) return <ErrorState error={integration.error} onRetry={() => integration.refetch()} />;
  const mailbox = integration.data;

  return (
    <IntegrationShell
      integration={mailbox}
      icon={<IntegrationLogo provider="FACILITIES_EMAIL" size="lg" />}
      title="Facilities email"
      description="Checks your facilities mailbox in Google Workspace or Microsoft 365. New emails become facilities requests that your routing rules assign, replies land on the request they belong to, and senders get a confirmation with their request number."
      tabs={[
        { value: 'connection', label: 'Connection', content: <EmailConnection integration={mailbox} /> },
        { value: 'settings', label: 'Senders & requests', content: <EmailOptions integration={mailbox} /> },
        { value: 'log', label: 'Email log', content: <EmailLog provider="FACILITIES_EMAIL" /> },
      ]}
    />
  );
}
